// src/App.js
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation
} from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './theme/ThemeContext';
import { colors } from './theme/tokens';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmModal';
import { InputProvider } from './components/InputModal';
import BubbleNav, { BUBBLE_NAV_HEIGHT } from './components/BubbleNav';
import PageTransition from './components/PageTransition';
import useIsDesktop from './hooks/useIsDesktop';
import Navbar from './components/Navbar';
import Login from './components/Login';
import Landing from './pages/Landing';
import Home from './pages/Home';
import AddData from './pages/AddData';
import FindData from './pages/FindData';
import Admin from './pages/Admin';
import ManageCarList from './pages/ManageCarList';
import ImportData from './pages/ImportData';
import FulfillRequests from './pages/FulfillRequests';
import ViewAllData from './pages/ViewAllData';
import ClientTracking from './pages/ClientTracking';
import SupportInbox from './pages/SupportInbox';
import ProfileMatch from './pages/ProfileMatch';
import NfcCardEditor from './pages/NfcCardEditor';
import PublicNfcCard from './pages/PublicNfcCard';
import TaskTracking from './pages/TaskTracking';

// Shell that wraps every authenticated route. It picks the right chrome
// (top tabs for sales team, plain navbar for everyone else) and renders the
// matched child route via <Outlet />. Because the route tree drives what is
// rendered, reloads preserve the user's exact sub-page.
function AppShell() {
  const { isAuthenticated, isSalesTeam, loading } = useAuth();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px', color: colors.text }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/welcome" replace state={{ from: location }} />;
  }

  // The PAR Car Parts sub-app keeps its in-app Navbar (Home / Find / Add + burger).
  const inCarParts =
    location.pathname === '/' || location.pathname.startsWith('/car-parts');

  // Sales team: gooey BubbleNav (top-level sections + profile). Desktop puts the
  // nav on top, mobile on the bottom; the profile bubble carries the account menu.
  if (isSalesTeam) {
    const paddingTop = isDesktop ? BUBBLE_NAV_HEIGHT : 0;
    const paddingBottom = isDesktop ? 0 : BUBBLE_NAV_HEIGHT;
    return (
      <div style={{ paddingTop, paddingBottom, minHeight: '100vh' }}>
        {inCarParts && <Navbar />}
        <Outlet />
      </div>
    );
  }

  // Non-sales users keep the single Navbar (which carries their account chrome).
  return (
    <>
      <Navbar />
      <Outlet />
    </>
  );
}

// /login route: bounce to the default landing page if already signed in.
function LoginGate() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px', color: colors.text }}>Loading...</div>;
  }
  return isAuthenticated ? <Navigate to="/car-parts/home" replace /> : <Login />;
}

// Public marketing landing page; signed-in users skip straight to the app.
function LandingGate() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px', color: colors.text }}>Loading...</div>;
  }
  return isAuthenticated ? <Navigate to="/car-parts/home" replace /> : <Landing />;
}

function AppContent() {
  // Consuming the theme here makes the whole routed tree re-render when the
  // user toggles light/dark, so every inline style re-reads the flipped tokens.
  const { mode } = useTheme();
  return (
    <div data-app-theme={mode} style={{ display: 'contents' }}>
    {/* Fixed nav chrome lives OUTSIDE PageTransition so the per-section
        page-fade key never remounts it (which caused flashing + lost animation). */}
    <BubbleNav />
    <PageTransition>
    <Routes>
      <Route path="/login" element={<LoginGate />} />
      <Route path="/welcome" element={<LandingGate />} />

      {/* ── Public NFC business card pages (no auth, no chrome) ─────────── */}
      <Route path="/card/:slug" element={<PublicNfcCard />} />

      <Route element={<AppShell />}>
        {/* Default landing → PAR Car Parts home */}
        <Route index element={<Navigate to="/car-parts/home" replace />} />

        {/* ── PAR Car Parts ─────────────────────────────── */}
        <Route path="car-parts">
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<Home />} />
          <Route path="find" element={<FindData />} />
          <Route path="add" element={<AddData />} />
          <Route path="fulfill" element={<FulfillRequests />} />
          <Route path="manage-cars" element={<ManageCarList />} />
          <Route path="import" element={<ImportData />} />
          <Route path="view-all" element={<ViewAllData />} />
        </Route>

        {/* ── Client Tracking (tab in the URL) ──────────── */}
        <Route path="client-tracking">
          <Route index element={<Navigate to="map" replace />} />
          <Route path=":tab" element={<ClientTracking />} />
        </Route>

        {/* ── Support Inbox (floating internal menu) ────── */}
        <Route path="support-inbox" element={<SupportInbox />} />

        {/* ── Profile Match (tab in the URL) ────────────── */}
        <Route path="profile-match">
          <Route index element={<Navigate to="capture" replace />} />
          <Route path=":tab" element={<ProfileMatch />} />
        </Route>

        {/* ── Task Tracking (ProAuto Rubber; tab in the URL) ────── */}
        <Route path="task-tracking">
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path=":tab" element={<TaskTracking />} />
        </Route>

        {/* ── NFC Business Card editor (rep-side) ────────────────── */}
        <Route path="nfc-card" element={<NfcCardEditor />} />

        {/* ── Admin Dashboard (top-level, controls every sub-app) ── */}
        <Route path="admin" element={<Admin />} />

        {/* ── Legacy URL redirects so existing bookmarks keep working ── */}
        <Route path="find" element={<Navigate to="/car-parts/find" replace />} />
        <Route path="add" element={<Navigate to="/car-parts/add" replace />} />
        <Route path="fulfill-requests" element={<Navigate to="/car-parts/fulfill" replace />} />
        <Route path="manage-cars" element={<Navigate to="/car-parts/manage-cars" replace />} />
        <Route path="import" element={<Navigate to="/car-parts/import" replace />} />
        <Route path="view-all" element={<Navigate to="/car-parts/view-all" replace />} />

        <Route path="*" element={<Navigate to="/car-parts/home" replace />} />
      </Route>
    </Routes>
    </PageTransition>
    </div>
  );
}

function App() {
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID_HERE';

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
            <InputProvider>
              <Router>
                <AuthProvider>
                  <AppContent />
                </AuthProvider>
              </Router>
            </InputProvider>
          </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
